import { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import logger from "@/utils/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-50 p-8 text-center">
          <div className="rounded-lg bg-white p-16 shadow-xl">
            <div className="mb-12 flex justify-center">
              <AlertCircle className="h-32 w-32 text-error-500" />
            </div>
            <h1 className="mb-8 text-2xl font-bold text-neutral-900">Something went wrong</h1>
            <p className="mb-12 text-neutral-700">
              We've encountered an unexpected error. Please try refreshing the page or contact
              support if the problem persists.
            </p>
            <div className="mb-12 rounded-md bg-neutral-100 p-8 text-left">
              <p className="mb-4 font-mono text-sm text-neutral-900">
                {this.state.error && this.state.error.toString()}
              </p>
              {this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-neutral-700">
                    View stack trace
                  </summary>
                  <pre className="mt-4 overflow-auto rounded bg-neutral-200 p-4 text-xs text-neutral-800">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
